import { useEffect, useRef, useState } from 'react';
import { v4 } from 'uuid';
import { useLocation } from 'react-router-dom';
import CssBaseline from '@mui/material/CssBaseline';
import { Stack, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import * as Sentry from "@sentry/react";
import mParticleSdk from '@mparticle/web-sdk';
import Cookies from 'js-cookie';

import './App.css';

import { initOpenTime, getOpenTime, getTimeSpent } from './utils.js';
import ProductDetailsCarousel from './web/components/ProductDetailsCarousel';
import useAppController from './web/hooks/useAppController';
import HomeExterior from './web/screens/HomeExterior';
import Home2D from './web/screens/Home2D';
import HomeVirtualTour from './web/screens/HomeVirtualTour';
import theme from './web/theme';
import EnvConfig from 'shared/libraries/mwc/envConfig';
import 'shared/libraries/mwc/3d/globalImports.js';

Sentry.init({
  dsn: "https://997c7fd79178432a9b340c3bf79142a4@sentry.modsy.com/12",
  integrations: [
    new Sentry.BrowserProfilingIntegration(),
    new Sentry.BrowserTracing({
      tracePropagationTargets: [
        /^https:\/\/www.modsy.com\/norma/,
        /^https:\/\/staging.modsy.com\/norma/,
        /^https:\/\/dev.modsy.com\/norma/,
        "localhost"
      ]
    })
  ],
  autoSessionTracking: true,
  tracesSampleRate: 1.0,
  profilesSampleRate: 1.0,
  environment: EnvConfig.EnvStrings[EnvConfig.getEnvironment()]
});

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const isBrowser = () => typeof window !== "undefined" && typeof window.document !== "undefined";
const MPARTICLE_API_KEY = "us1-0579633ea8846a43abee653a45ea4049";

function AppWeb () {
  const [fbp, setFbp] = useState(null);
  function logEvent (eventName, eventAttributes) {
    let defaultAttributes = {};
    const expectedAttributes = [
      "optId", "communityId", "Comnum", "mpcId", "planId", "planNum", "homesiteId", "pageviewId", "impressionId", "session_uuid", "mpDeviceId", "marketCode", "impressionName"
    ];
    for (let attribute of expectedAttributes) {
      defaultAttributes[attribute] = query.get(attribute) ?? ""
    }
    if (defaultAttributes.pageviewId && defaultAttributes.impressionId) {
      mParticleInstance.current?.logEvent(
        eventName,
        mParticle.EventType.Other,
        {
          "event_type": mParticle.EventType.Other,
          "data": {
            "custom_event_type": mParticle.EventType.Other,
            "custom_flags": {
                "Facebook.BrowserId": fbp ?? "",
                "Facebook.browserId": fbp ?? "",
                "Facebook.ClickId": null,
                "Facebook.ActionSource": "website"
            },
            "event_name": eventName,
            "timestamp_unixtime_ms": Date.now(),
            "session_uuid": defaultAttributes["session_uuid"] ?? "",
            "session_start_unixtime_ms": getOpenTime(),
            "custom_attributes": {
                ...defaultAttributes,
                "eventTimestamp": Date.now(),
                "screenDimensions": `{\"height\":${window.innerHeight},\"width\":${window.innerWidth}`,
                "Lennar Environment": EnvConfig.getEnvName() === "prod" ? "Production" : "Test",
                "listingId": "",
                "interactionId": v4(),
                "interactionType": "click",
                "placement": "main",
                "clickDestination": "self",
                "clickText": "",
                "clickLabel": "",
                "formData": null,
            },
            "location": {
                "latitude": eventAttributes.clickLocation?.x,
                "longitude": eventAttributes.clickLocation?.y
            },
            "source_message_id":  defaultAttributes["session_uuid"] ?? "",
            "active_time_on_site_ms": getTimeSpent()
          }
        }
      );
    }
  }
  const logEventRef = useRef(logEvent);
  const [state, dispatch] = useAppController(logEventRef);
  const { apiDataLoaded, error } = state;
  const mParticleInstance = useRef(null);
  const query = useQuery();

  useEffect(() => {
    initOpenTime();
    // Init mParticle
    if (isBrowser() && !mParticleInstance.current) {
      const fbCookie = Cookies.get("_fbp");
      if (fbCookie) {
        setFbp(fbCookie)
      }
      let mParticleConfig = {
        isDevelopmentMode: EnvConfig.getEnvName() !== "prod",
        dataPlan: {
            planId: "lennar_web_data_plan",
        },
        useCookieStorage: true,
        identityCallback: (result) => () => {},
      };
      mParticleSdk.init(MPARTICLE_API_KEY, mParticleConfig);
      mParticleInstance.current = mParticleSdk;
      mParticleInstance.current?.ready(
        function() {
          console.log("mParticle has loaded!");
        }
      );
    }

    // Track Mouse Click/Drag Events on Tracked Components
    const handleMouseDown = (e) => {
      const trackedElement = e.target.closest("[data-track-id]");
      if (trackedElement) {
        // Tracked Component click
        logEventRef.current(
          trackedElement.getAttribute("data-track-value"),
          { clickLocation: { x: e.clientX, y: e.clientY } }
        );
      }
    };

    // Hook Event Listener
    window.addEventListener("mousedown", handleMouseDown);
    return () => {
      window.removeEventListener("mousedown", handleMouseDown);
    };

  }, []);


  return (
      <div id="topLevelContainer">
        <ThemeProvider theme={theme}>
          <CssBaseline />

          {error && (
            <Stack direction="column" justifyContent="center" alignItems="center" height={1.0}>
              <Typography variant="h1" fontWeight="bold">{error.statusCode ?? "Error!"}</Typography>
              <Typography variant="body1">{error.message}</Typography>
            </Stack>
          )}

          {apiDataLoaded && (
            <HomeExterior
              dispatch={dispatch}
              state={state}
            />
          )}

          {apiDataLoaded && (
            <Home2D
              dispatch={dispatch}
              state={state}
            />
          )}

          <HomeVirtualTour
            dispatch={dispatch}
            state={state}
          />

          <ProductDetailsCarousel
            dispatch={dispatch}
            state={state}
          />
        </ThemeProvider>
      </div>
  );
}

export default Sentry.withProfiler(AppWeb);
